import React, {
  useEffect, useState, useRef, memo,
} from 'react';
import { func } from 'prop-types';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import pick from 'lodash/pick';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import IFrame from '../IFrame';
import getbrowserFieldsFor3ds from './utils/getbrowserFieldsFor3ds';
import {
  usePre3dsMutation,
  useInit3dsMutation,
  useCom3dsMutation,
} from '../../../store/services/common/make3ds/make3dsApi';
import {
  useLogTrackMutation,
} from '../../../store/services/common/logTrack/logTrackApi';
import { setMake3dsFlow, MAKE3DSFLOW } from './make3dsSlice';
import { setErrorMessage } from '../../../store/slices/errorSlice';
import { showLoadder, hideLoadder } from '../../../store/slices/loadderSlice';
import { isOriginAcceptable } from '../IFrame/messageHandlers';

const ORIGIN_REGEX_STR_FOR_3DS = '\\.cardinalcommerce\\.com/?$';

const Make3ds = ({
  submitService, loadAllData,
}) => {
  const timer = useRef();
  const preform = useRef();
  const initForm = useRef();
  const dispatch = useDispatch();
  const [ pre3dsResult, setPre3dsResult ] = useState({});
  const [ init3dsResult, setInit3dsResult ] = useState({});
  const {
    returnUrl, widgetNameSpace,
  } = useSelector(state => state.propsData);
  const {
    make3dsFlow, submitParams,
  } = useSelector(state => state.make3ds);

  const [ pre3ds ] = usePre3dsMutation();
  const [ init3ds ] = useInit3dsMutation();
  const [ com3ds ] = useCom3dsMutation();
  const [ logTrack ] = useLogTrackMutation();
  const getCommonSubmitParams = params => pick(params, [
    'paymentOptionType',
    'billingContact',
    'paymentAccountId',
    'useCreditBalance',
    'customAmount',
    'paymentPlanOptions' ]);

  const handlePre3ds = () => {
    const params = getCommonSubmitParams(submitParams);
    pre3ds({ params }).unwrap().then((res) => {
      setPre3dsResult(res);
    }).catch(() => {
      dispatch(hideLoadder());
    });
  };

  useEffect(() => {
    const { postUrl: pre3dsPostUrl, stateOf3DS } = pre3dsResult;
    if (!isEmpty(pre3dsResult)) {
      if (pre3dsPostUrl) {
        setTimeout(() => {
          preform?.current?.submit();
        }, 10);
        timer.current = setTimeout(() => {
          dispatch(setErrorMessage({ type: 'danger' }));
          dispatch(hideLoadder());
          logTrack?.({
            params: {
              message: 'pre3ds form no response.',
            },
          });
        }, 30000);
      } else {
        submitService({ ...submitParams, stateOf3DS }, false);
      }
    }
  }, [ pre3dsResult ]);

  const handleInit3ds = () => {
    const { transId: pre3dsTransId } = pre3dsResult;
    const commonSubmitParams = getCommonSubmitParams(submitParams);
    const params = {
      ...commonSubmitParams,
      pre3dsTransId,
      returnUrl,
      browserData: omitBy(getbrowserFieldsFor3ds(), isUndefined),
    };
    init3ds({ params }).unwrap().then((res) => {
      setInit3dsResult(res);
    }).catch(() => {
      dispatch(hideLoadder());
    });
  };

  useEffect(() => {
    if (!isEmpty(init3dsResult)) {
      const { stateOf3DS, postUrl: init3dsPostUrl } = init3dsResult;
      if (stateOf3DS) {
        submitService({ ...submitParams, stateOf3DS }, false);
      } else if (init3dsPostUrl) {
        const mdData = JSON.stringify({
          submitParams,
          init3dsResult,
        });
        sessionStorage.setItem(`${widgetNameSpace}-checkoutMDData`, mdData); // save MD in sessionStorage
        initForm?.current?.submit(); // Redirect to bank page
      } else {
        dispatch(hideLoadder());
        dispatch(setErrorMessage({ type: 'danger' }));
        logTrack?.({
          params: {
            message: 'init3ds failed.',
          },
        });
      }
    }
  }, [ init3dsResult ]);

  const handleCom3ds = ({ init3dsResultData, submitParam }) => {
    const { transId: init3dsTransId } = init3dsResultData;
    const commonSubmitParams = getCommonSubmitParams(submitParam);
    const params = {
      ...commonSubmitParams,
      init3dsTransId,
    };
    com3ds({ params }).unwrap().then((res) => {
      const { stateOf3DS } = res;
      if (stateOf3DS) {
        submitService({ ...submitParam, stateOf3DS }, true);
      } else {
        loadAllData();
        dispatch(hideLoadder());
        dispatch(setErrorMessage({ type: 'danger' }));
        logTrack?.({
          params: {
            message: 'com3ds failed.',
          },
        });
      }
    }).catch(() => {
      loadAllData();
      dispatch(hideLoadder());
    });
  };

  useEffect(() => {
    if (make3dsFlow === MAKE3DSFLOW.PRE3DSFLOW) {
      handlePre3ds();
      dispatch(setMake3dsFlow(MAKE3DSFLOW.NONEFLOW));
    } else if (make3dsFlow === MAKE3DSFLOW.COM3DSFLOW) { // Bank redirect success
      dispatch(showLoadder());
      const sessionMDData = sessionStorage.getItem(`${widgetNameSpace}-checkoutMDData`);
      const mdData = JSON.parse(sessionMDData);
      const {
        init3dsResult: init3dsResultData,
        submitParams: submitParam,
      } = mdData;
      handleCom3ds({ init3dsResultData, submitParam });
    }
  }, [ make3dsFlow ]);

  // Temporary: Record 3ds origin
  const handleLogTrack = (origin) => {
    if (!isOriginAcceptable(origin, ORIGIN_REGEX_STR_FOR_3DS)) {
      logTrack?.({
        params: {
          message: `log3dsOrigin:${origin}`,
        },
      });
    }
  };

  const receiveMessage = ({ SessionId: sessionId }, { origin }) => {
    if (sessionId) {
      clearTimeout(timer.current);
      handleLogTrack(origin);
      handleInit3ds();
    }
  };

  const { postUrl, jwt } = pre3dsResult;
  const { postUrl: init3dsFormUrl, jwt: init3dsJwt } = init3dsResult;
  const md = JSON.stringify({
    bankRedirectSuccess: true,
  });

  return (
    <div className="checkout-ui-component make3ds">
      <IFrame
        className="pre3ds-iframe"
        style={{ display: 'none' }}
        title='pre3ds iframe'
        name="pre3ds_iframe"
        listener={receiveMessage}>
        <form
          target="pre3ds_iframe"
          name='pre3ds-form'
          action={postUrl}
          ref={preform}
          method="POST">
          <input name="JWT" value={jwt} hidden/>
        </form>
      </IFrame>
      <form
        name='init3ds-form'
        action={init3dsFormUrl}
        method="post"
        ref={initForm}>
        <input name="JWT" value={init3dsJwt} hidden/>
        <input name="MD" value={md} hidden/>
      </form>
    </div>
  );
};

Make3ds.propTypes = {
  submitService: func.isRequired,
  loadAllData: func.isRequired,
};

export default memo(Make3ds);
